import { useMemo, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Contact, Home, MessageSquare, Phone } from "lucide-react";
import { ROUTES } from "@/constants/routes";
import { queryKeys } from "@/constants/queryKeys";
import { fetchChatConversations } from "@/services/api/chat";
import { usePhoneStore, type RegStatus } from "@/features/phone";
import { cn } from "@/lib/cn";
import { initialsFromName } from "@/lib/media";
import { useAuthStore } from "@/store";

const navItems = [
  { to: ROUTES.dashboard, label: "Home", icon: Home, end: true },
  { to: ROUTES.messages, label: "Messages", icon: MessageSquare, end: false },
  { to: ROUTES.calls, label: "Calls", icon: Phone, end: false },
  { to: ROUTES.contacts, label: "Contacts", icon: Contact, end: false },
];

function sipStatusLabel(status: RegStatus) {
  if (status === "online") return "Asterisk connected";
  if (status === "connecting") return "Connecting to phone…";
  return "Phone disconnected";
}

export function FloatingRail() {
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const regStatus = usePhoneStore((state) => state.regStatus);
  const navigate = useNavigate();
  const [avatarFailed, setAvatarFailed] = useState(false);
  const showImage = Boolean(user?.profilePic) && !avatarFailed;
  const statusTitle = sipStatusLabel(regStatus);

  const { data: conversations = [] } = useQuery({
    queryKey: queryKeys.chatConversations(""),
    queryFn: () => fetchChatConversations(),
    enabled: isAuthenticated,
    staleTime: 15_000,
    refetchOnWindowFocus: true,
  });

  const unreadTotal = useMemo(
    () =>
      conversations.reduce((sum, item) => sum + (Number(item.unread) || 0), 0),
    [conversations],
  );

  return (
    <aside className="relative z-20 flex w-[68px] shrink-0 flex-col items-center py-3">
      <div
        className="flex h-full w-[56px] flex-col items-center rounded-[32px] px-1.5 py-3 text-white shadow-[0_14px_30px_rgba(65,33,10,0.2)]"
        style={{ background: "var(--rail)" }}
      >
        <NavLink
          to={ROUTES.dashboard}
          className="mb-4 flex flex-col items-center gap-1"
          title="Papaya"
        >
          <img
            src="/logo.png"
            alt="Papaya"
            className="h-9 w-9 rounded-full bg-white/90 object-cover p-0.5 shadow-sm"
          />
        </NavLink>

        <nav className="flex w-full flex-1 flex-col items-center gap-1.5">
          {navItems.map((item) => {
            const showUnread =
              item.to === ROUTES.messages && unreadTotal > 0;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                title={
                  showUnread
                    ? `${item.label} (${unreadTotal} unread)`
                    : item.label
                }
                className={({ isActive }) =>
                  cn(
                    "group flex w-full flex-col items-center gap-0.5 text-[9px] font-medium text-white/85 transition-colors",
                    isActive && "text-white",
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <span
                      className={cn(
                        "relative inline-flex h-10 w-10 items-center justify-center transition-all",
                        "rounded-[32px]",
                        isActive
                          ? "bg-white/25 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.25)]"
                          : "hover:bg-white/15",
                      )}
                    >
                      <item.icon className="h-4 w-4" strokeWidth={2.1} />
                      {showUnread ? (
                        <span className="absolute -top-0.5 -right-0.5 inline-flex h-[16px] min-w-[16px] items-center justify-center rounded-full bg-[#ff6b35] px-1 text-[9px] font-bold text-white shadow-sm ring-2 ring-[var(--rail)]">
                          {unreadTotal > 99 ? "99+" : unreadTotal}
                        </span>
                      ) : null}
                    </span>
                    <span className="leading-none">{item.label}</span>
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="mt-2 flex w-full flex-col items-center gap-1.5">
          <button
            type="button"
            title={`Profile settings${user?.name ? ` (${user.name})` : ""} — ${statusTitle}`}
            aria-label={`${user?.name || "Profile"}; ${statusTitle}`}
            onClick={() => navigate(ROUTES.settings)}
            className="relative inline-flex h-10 w-10 items-center justify-center rounded-[32px] bg-white/20 text-white hover:bg-white/30"
          >
            <span className="inline-flex h-full w-full items-center justify-center overflow-hidden rounded-[32px]">
              {showImage ? (
                <img
                  src={user!.profilePic!}
                  alt=""
                  className="h-full w-full object-cover"
                  onError={() => setAvatarFailed(true)}
                />
              ) : (
                <span className="text-[11px] font-bold uppercase">
                  {initialsFromName(user?.name || "A")}
                </span>
              )}
            </span>
            <span
              className={cn(
                "absolute right-0 bottom-0 h-2.5 w-2.5 rounded-full ring-2 ring-[var(--rail)]",
                regStatus === "online" && "bg-emerald-400",
                regStatus === "connecting" && "animate-pulse bg-amber-400",
                regStatus === "offline" && "bg-zinc-400",
              )}
              title={statusTitle}
              aria-hidden
            />
          </button>
        </div>
      </div>
    </aside>
  );
}
